import { Divider, Grid } from "@mui/material";
import { Stack } from "@mui/system";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { calcProPercentsBasedOnFilterAndDays, itemPercentage, percentageLabel } from "../../../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../../../utilis/percentage-comparison";
import { COLORS_BOX_CODE } from "../../../../types/common.type";
import ColorBox from "../../../../components/shared/dashboard/ColorBox";
import styles from "./TotalOverallCard.module.scss";

const TotalOverallIncomingCard = ({
	total,
	totalComparison,
	page,
	isProjection = false,
	totalProjection = 0,
	totalProjectionPercentage,
	incomingDetailsTotal,
	incomingDetailsTotalComparison,
	subTitleText = "Avg. Cencus",
	isCompareColor,
	averageCensus,
	filter,
	filterComparison,
	averageCensusComparison,
	loading,
	projectionDays,
	filterTotal,
	isLayoutMode = false,
	admissionCompareAgainst = null, // bedCapacity equivalent for incoming transfers (like Admission page)
	admissionCompareAgainstComparison = null, // bedCapacity comparison equivalent
}) => {
	const percentage = itemPercentage(total, averageCensus, "number", page, true);
	const percentageComparison = itemPercentage(totalComparison, averageCensusComparison, "number", page, true);
	const { percentageDiff, itemTotalComparisonScaled, numberOfDays } = calculateDiffBetweenPercentages(
		percentage,
		percentageComparison,
		true,
		filter,
		filterComparison,
		totalComparison,
		projectionDays
	);
	// Use true for isSpecialPage since incoming transfers are similar to Admission page
	const comparisonDiffColor = pickComparisonColor(percentageDiff, true);

	return (
		<div className={`df aic p-t-20 p-b-25 ${styles.totalOverall} ${styles.withSubTotalCardMain}`}>
			<Grid container direction={"row"}>
				<Grid item xs={6}>
					<Stack direction={"column"} justifyContent={"center"} alignItems={"center"}>
						<Stack item direction={"column"} alignContent={"center"}>
							<div
								className={classNames(
									"ffmar-bold",
									"df",
									"aic",
									"fw700",
									"fs49",
									styles.gradientTextPrimary,
									styles.withSubTotalCard,
									loading && styles.skeleton
								)}
							>
								{loading ? (
									<span className="skeleton-text">0</span>
								) : (
									calcProPercentsBasedOnFilterAndDays(total, filter, projectionDays)
								)}
								{isCompareColor && !loading && (
									<ColorBox
										color={comparisonDiffColor}
										comparingAgainst={totalComparison}
										comparingAgainstScaled={itemTotalComparisonScaled}
										numberOfDays={numberOfDays}
										isTotalOverallCard={true}
									/>
								)}
							</div>
							{percentage && (
								<div className={`${styles.textNumberContainer} ${styles.textNumberSubContainer}`}>
									<div
										className={classNames(
											styles.gradientTextSecondary,
											loading && styles.skeleton,
											loading && "skeleton-text"
										)}
									>{`(${
										loading
											? 0
											: calcProPercentsBasedOnFilterAndDays(
													percentage,
													filter,
													projectionDays,
													filterTotal,
													averageCensus
											  )
									})%`}</div>
									<div className={classNames(styles.gradientTextSubtitle, loading && "skeleton-text")}>
										{loading ? (
											<span className="skeleton-text" style={{ visibility: "hidden" }}>
												{subTitleText}
											</span>
										) : (
											subTitleText
										)}
									</div>
								</div>
							)}
						</Stack>
					</Stack>
				</Grid>
				<Divider orientation="vertical" flexItem />
				<Grid item xs={5}>
					<Stack
						direction={"column"}
						spacing={1}
						justifyContent={"center"}
						alignContent={"center"}
						alignItems={"center"}
						sx={{ height: "100%" }}
					>
						<SubTotalCardItem
							title={"Admission"}
							loading={loading}
							total={calcProPercentsBasedOnFilterAndDays(
								incomingDetailsTotal?.totalAdmission || 0,
								filter,
								projectionDays
							)}
							totalComparisonRaw={incomingDetailsTotalComparison?.totalAdmission || 0}
							totalRaw={incomingDetailsTotal?.totalAdmission || 0}
							averageCensus={averageCensus}
							averageCensusComparison={averageCensusComparison}
							admissionCompareAgainst={admissionCompareAgainst}
							admissionCompareAgainstComparison={admissionCompareAgainstComparison}
							percentage={percentageLabel(
								calcProPercentsBasedOnFilterAndDays(
									itemPercentage(incomingDetailsTotal?.totalAdmission || 0, total, "number"),
									filter,
									projectionDays,
									filterTotal,
									averageCensus
								)
							)}
							filter={filter}
							filterComparison={filterComparison}
							projectionDays={projectionDays}
							isCompareColor={isCompareColor}
							isLayoutMode={isLayoutMode}
							page={page}
						/>
						<SubTotalCardItem
							title={"Readmission"}
							loading={loading}
							total={calcProPercentsBasedOnFilterAndDays(
								incomingDetailsTotal?.totalReAdmission || 0,
								filter,
								projectionDays
							)}
							totalComparisonRaw={incomingDetailsTotalComparison?.totalReAdmission || 0}
							totalRaw={incomingDetailsTotal?.totalReAdmission || 0}
							averageCensus={averageCensus}
							averageCensusComparison={averageCensusComparison}
							admissionCompareAgainst={admissionCompareAgainst}
							admissionCompareAgainstComparison={admissionCompareAgainstComparison}
							percentage={percentageLabel(
								calcProPercentsBasedOnFilterAndDays(
									itemPercentage(incomingDetailsTotal?.totalReAdmission || 0, total, "number"),
									filter,
									projectionDays,
									filterTotal,
									averageCensus
								)
							)}
							filter={filter}
							filterComparison={filterComparison}
							projectionDays={projectionDays}
							isCompareColor={isCompareColor}
							isLayoutMode={isLayoutMode}
							page={page}
						/>
						<SubTotalCardItem
							title={"Hospital Return"}
							loading={loading}
							total={calcProPercentsBasedOnFilterAndDays(
								incomingDetailsTotal?.totalHospitalReturn || 0,
								filter,
								projectionDays
							)}
							totalComparisonRaw={incomingDetailsTotalComparison?.totalHospitalReturn || 0}
							totalRaw={incomingDetailsTotal?.totalHospitalReturn || 0}
							averageCensus={averageCensus}
							averageCensusComparison={averageCensusComparison}
							admissionCompareAgainst={admissionCompareAgainst}
							admissionCompareAgainstComparison={admissionCompareAgainstComparison}
							percentage={percentageLabel(
								calcProPercentsBasedOnFilterAndDays(
									itemPercentage(incomingDetailsTotal?.totalHospitalReturn || 0, total, "number"),
									filter,
									projectionDays,
									filterTotal,
									averageCensus
								)
							)}
							filter={filter}
							filterComparison={filterComparison}
							projectionDays={projectionDays}
							isCompareColor={isCompareColor}
							isLayoutMode={isLayoutMode}
							page={page}
						/>
					</Stack>
				</Grid>
			</Grid>
		</div>
	);
};

export default TotalOverallIncomingCard;

const SubTotalCardItem = ({ 
	title, 
	total, 
	totalRaw,
	totalComparisonRaw,
	averageCensus,
	averageCensusComparison,
	percentage, 
	loading,
	filter,
	filterComparison,
	projectionDays,
	isCompareColor,
	isLayoutMode,
	page,
	admissionCompareAgainst = null, // bedCapacity equivalent (like Admission page)
	admissionCompareAgainstComparison = null, // bedCapacity comparison equivalent
}) => {
	const [comparisonDiffColor, setComparisonDiffColor] = useState(null);
	const [comparingAgainstScaled, setComparingAgainstScaled] = useState(null);
	const [comparingAgainst, setComparingAgainst] = useState("");
	const [numberOfDays, setNumberOfDays] = useState(null);
	const percentageThresholds = useSelector((state) => state.comparison.percentageThresholds);
	const rangesSet = useSelector((state) => state.comparison.rangesSet);

	useEffect(() => {
		if (!isCompareColor || loading) {
			setComparisonDiffColor(null);
			setComparingAgainstScaled(null);
			setComparingAgainst("");
			setNumberOfDays(null);
			return;
		}

		// For incoming transfers (special page like Admission), calculate percentages using admissionCompareAgainst (bedCapacity)
		// This matches the pattern in TotalTransferCard for special pages
		// Use admissionCompareAgainst if provided (bedCapacity), otherwise fallback to averageCensus
		const compareAgainst = admissionCompareAgainst !== null ? Number(admissionCompareAgainst) || 0 : Number(averageCensus) || 0;
		const compareAgainstComparison = admissionCompareAgainstComparison !== null ? Number(admissionCompareAgainstComparison) || 0 : Number(averageCensusComparison) || 0;
		const totalParsed = itemPercentage(totalRaw || 0, compareAgainst, "number");
		const totalComparisonParsed = itemPercentage(totalComparisonRaw || 0, compareAgainstComparison, "number");

		// Use true for isSpecialPage since incoming transfers are similar to Admission page
		// This reverses the sign of percentageDiff (forAdmission = true reverses the result)
		const {
			percentageDiff,
			itemTotalComparisonScaled,
			numberOfDays: numberOfDaysTemp,
		} = calculateDiffBetweenPercentages(
			totalParsed,
			totalComparisonParsed,
			true, // forAdmission = true for incoming transfers (like Admission) - reverses the sign
			filter,
			filterComparison,
			totalComparisonRaw || 0,
			projectionDays
		);

		setNumberOfDays(numberOfDaysTemp);
		setComparingAgainstScaled(itemTotalComparisonScaled);
		// Use raw comparison value (matching TotalTransferCard pattern)
		setComparingAgainst(totalComparisonRaw || 0);

		let percentageChange = pickComparisonColor(percentageDiff, true); // true for special page
		
		// Workaround: If percentageDiff is exactly at littleGoodValue and littleGood.showColor is true,
		// show the color (pickComparisonColor returns null for boundary values <= littleGoodValue)
		// This matches the behavior expected when the value is just above the threshold
		if (percentageChange === null && percentageDiff >= 0 && rangesSet) {
			const littleGoodValue = percentageThresholds?.littleGood?.value || 0;
			if (percentageDiff === littleGoodValue && percentageThresholds?.littleGood?.showColor) {
				// Use the same color as the next range (littleGood range)
				percentageChange = COLORS_BOX_CODE.LIGHT_GREEN;
			}
		}
		setComparisonDiffColor(percentageChange);
	}, [
		title,
		totalRaw,
		totalComparisonRaw,
		averageCensus,
		averageCensusComparison,
		admissionCompareAgainst,
		admissionCompareAgainstComparison,
		filter,
		filterComparison,
		projectionDays,
		isCompareColor,
		loading,
		percentageThresholds,
		rangesSet,
	]);

	return (
		<Stack item sx={{ width: "90%", borderRadius: "5px" }} className={loading ? "skeleton" : undefined}>
			<div className={classNames(styles.projectionContainer, styles.withSubTotalCard, loading && styles.skeleton)}>
				<div className={`${styles.projectionTitle}`}>
					{loading ? (
						<span className="skeleton-text" style={{ visibility: "hidden" }}>
							{title}
						</span>
					) : (
						title
					)}
				</div>
				<div className={`${styles.projectionSubTitle}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "row", gap: "4px" }}>
					{loading ? (
						<span className="skeleton-text" style={{ visibility: "hidden" }}>
							{total} {percentage}
						</span>
					) : (
						<>
							<span style={{ order: 1 }}>{`${total} ${percentage}`}</span>
							{isCompareColor && !loading && (
								<span style={{ order: 2 }}>
									<ColorBox
										color={comparisonDiffColor}
										comparingAgainst={comparingAgainst}
										comparingAgainstScaled={comparingAgainstScaled}
										numberOfDays={numberOfDays}
										loading={loading}
										loadingComparison={false}
										isLayoutMode={isLayoutMode}
										sx={{ height: "18px", width: "9px" }}
										isTotalOverallCard={true}
									/>
								</span>
							)}
						</>
					)}
				</div>
			</div>
		</Stack>
	);
};
